'use client';

import { ContentContainer } from '@module/mdrt-org/shared/components';
import { type MemberActivity } from '@module/mdrt-org/shared/utils/types/public-customer-profile';
import { Pagination, usePagination } from '@shared/ui-components';

import { type MemberDirectoryProfilePageContent } from '../get-member-directory-profile-page-content';

import styles from './additional-profile-details-section.module.scss';

const PARENT_CLASSNAME = 'additional-profile-details-section';

const extractYearFromISODate = (isoDate: string): number => {
  const date = new Date(isoDate);
  return date.getUTCFullYear();
};

export const AdditionalProfileDetailsSection = ({
  cmsContent,
  memberActivity,
}: {
  cmsContent: MemberDirectoryProfilePageContent;
  memberActivity: MemberActivity | undefined;
}) => {
  const {
    currentPage: currentCommitteeDetailsTablePage,
    endIndex,
    setCurrentPage: setCurrentCommitteeDetailsTablePage,
    startIndex,
  } = usePagination({});

  return (
    <div className={styles[PARENT_CLASSNAME]}>
      <ContentContainer>
        <div className={styles[`${PARENT_CLASSNAME}__container`]}>
          <div className={styles[`${PARENT_CLASSNAME}__container-item`]}>
            <div className={styles['additional-details']}>
              <h2 className={styles[`${PARENT_CLASSNAME}__heading`]}>
                {cmsContent.additionalInformationBlock.title}
              </h2>
              <dl className={styles['data-list']}>
                <dt className={styles['data-list__label']}>
                  {cmsContent.additionalInformationBlock.mdrtIdlabel}:
                </dt>
                <dd className={styles['data-list__value']}>{memberActivity?.mdrtId ?? '-'}</dd>
                <dt className={styles['data-list__label']}>
                  {cmsContent.additionalInformationBlock.ageLabel}:
                </dt>
                <dd className={styles['data-list__value']}>{memberActivity?.age ?? '-'}</dd>
                <dt className={styles['data-list__label']}>
                  {cmsContent.additionalInformationBlock.birthdateLabel}:
                </dt>
                <dd className={styles['data-list__value']}>
                  {memberActivity?.birthday ? extractYearFromISODate(memberActivity.birthday) : '-'}
                </dd>
                <dt className={styles['data-list__label']}>
                  {cmsContent.additionalInformationBlock.firstLastMembershipLabel}
                </dt>
                <dd className={styles['data-list__value']}>
                  {memberActivity?.firstYear ?? '-'}/{memberActivity?.lastYear ?? '-'}
                </dd>
                <dt className={styles['data-list__label']}>
                  {cmsContent.additionalInformationBlock.yearsOfMembershipLevelLabel}
                </dt>
                <dd className={styles['data-list__value']}>
                  {memberActivity?.totalMembership ?? '-'}/{memberActivity?.totalCOT ?? '-'}/
                  {memberActivity?.totalCOT ?? '-'}
                </dd>
                <dt className={styles['data-list__label']}>
                  {cmsContent.additionalInformationBlock.annualMeetingsCountLabel}
                </dt>
                <dd className={styles['data-list__value']}>
                  {memberActivity?.annualMeetingsAttendedCount ?? '-'}/
                  {memberActivity?.lastAnnualMeetingAttended ?? '-'}
                </dd>
                <dt className={styles['data-list__label']}>
                  {cmsContent.additionalInformationBlock.mdrtFundationLevelLabel}
                </dt>
                <dd className={styles['data-list__value']}>{memberActivity?.donorLevel ?? '-'}</dd>
              </dl>
            </div>
            <div className={styles['committee-summary']}>
              <h2 className={styles[`${PARENT_CLASSNAME}__heading`]}>
                {cmsContent.committeeSummaryTable.title}
              </h2>
              <div className="table-wrapper">
                <table className="table">
                  <thead className="table__header">
                    <tr>
                      <th />
                      <th>{cmsContent.committeeSummaryTable.engagementLabel}</th>
                      <th>{cmsContent.committeeSummaryTable.committeeLabel}</th>
                      <th>{cmsContent.committeeSummaryTable.leadershipLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>{cmsContent.committeeSummaryTable.jobsLabel}</th>
                      <td>{memberActivity?.engagementJobsCount}</td>
                      <td>{memberActivity?.committeeJobsCount}</td>
                      <td>{memberActivity?.leadershipJobsCount}</td>
                    </tr>
                    <tr>
                      <th>{cmsContent.committeeSummaryTable.averageLabel}</th>
                      <td>{memberActivity?.averageEngagementJobsPercentage}</td>
                      <td>{memberActivity?.averageCommitteeJobsPercentage}</td>
                      <td>{memberActivity?.averageLeadershipJobsPercentage}</td>
                    </tr>
                    <tr>
                      <th>{cmsContent.committeeSummaryTable.totalPointsLabel}</th>
                      <td>{memberActivity?.totalEngagementPoints}</td>
                      <td>{memberActivity?.totalCommitteePoints}</td>
                      <td>{memberActivity?.totalLeadershipPoints}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className={styles[`${PARENT_CLASSNAME}__container-item`]}>
            <div className={styles['committee-details']}>
              <h2 className={styles[`${PARENT_CLASSNAME}__heading`]}>
                {cmsContent.committeeDetailsTable.title}
              </h2>
              <div className="table-wrapper">
                <table className="table">
                  <thead className="table__header">
                    <tr>
                      <th>{cmsContent.committeeDetailsTable.yearLabel}</th>
                      <th>{cmsContent.committeeDetailsTable.divisionLabel}</th>
                      <th>{cmsContent.committeeDetailsTable.committeeLabel}</th>
                      <th>{cmsContent.committeeDetailsTable.roleLabel}</th>
                      <th>{cmsContent.committeeDetailsTable.pointsPossibleLabel}</th>
                      <th>{cmsContent.committeeDetailsTable.pointsEarnedLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberActivity?.committeeDetails.slice(startIndex, endIndex).map((item) => (
                      <tr key={item.year + item.committee + item.division}>
                        <td>{item.year}</td>
                        <td>{item.division}</td>
                        <td>{item.committee}</td>
                        <td>{item.role}</td>
                        <td>{item.pointsPossible}</td>
                        <td>{item.pointsEarned}</td>
                      </tr>
                    ))}
                    {!memberActivity?.committeeDetails?.length && (
                      <tr>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {Boolean(
                memberActivity?.committeeDetails?.length &&
                  memberActivity?.committeeDetails?.length > 0
              ) && (
                <Pagination
                  className={styles['pagination']}
                  initialPage={currentCommitteeDetailsTablePage}
                  onPageChange={(index) => {
                    setCurrentCommitteeDetailsTablePage(index);
                  }}
                  totalItemsCount={memberActivity?.committeeDetails?.length}
                />
              )}
            </div>
          </div>
        </div>
      </ContentContainer>
    </div>
  );
};
